import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../utils/config';

interface Construction {
  constructionId: number;
  appraisalId: number;
  appraisalName: string;
  name: string;
  fileName: string;
  fileSize: number;
  worksheetCount: number;
  status: string;
  createdAt: string;
  documentsCount?: number;
  dataSetsCount?: number;
}

export const XlsxConstructionAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [constructions, setConstructions] = useState<Construction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch all constructions across appraisals
  const fetchConstructions = useCallback(async () => {
    try {
      setError(null);
      // Note: This endpoint would need to be created for admin access
      const response = await authenticatedFetch(`${API_URL}/api/appraisals/admin/constructions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setConstructions(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch constructions');
      }
    } catch (err) {
      console.error('Error fetching constructions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch constructions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchConstructions();
  }, [fetchConstructions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchConstructions();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded': return 'success';
      case 'processing': return 'warning';
      case 'error': return 'error';
      case 'identified': return 'info';
      default: return 'default';
    }
  };

  const handleViewConstruction = (constructionId: number) => {
    // Navigate to a construction detail view - this would need to be implemented
    navigate(`/admin/appraisals/construction/xlsx/${constructionId}`);
  };

  const handleViewAppraisal = (appraisalId: number) => {
    navigate(`/appraisals/admin/${appraisalId}/constructions`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Paginate constructions
  const paginatedConstructions = constructions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/dashboard"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <HomeIcon fontSize="inherit" />
          Dashboard
        </Link>
        <Link 
          color="inherit" 
          href="/appraisals"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AssessmentIcon fontSize="inherit" />
          Appraisals
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AdminIcon fontSize="inherit" />
          Excel Construction Admin
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Excel Construction Administration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage Excel workbook constructions across all appraisals
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Constructions Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Construction Name</TableCell>
                <TableCell>Appraisal</TableCell>
                <TableCell>Excel File</TableCell>
                <TableCell>File Size</TableCell>
                <TableCell>Worksheets</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Data Sets</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedConstructions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No Excel constructions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedConstructions.map((construction) => (
                  <TableRow key={construction.constructionId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {construction.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleViewAppraisal(construction.appraisalId)}
                      >
                        {construction.appraisalName}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {construction.fileName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(construction.fileSize)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {construction.worksheetCount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {construction.documentsCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {construction.dataSetsCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={construction.status}
                        color={getStatusColor(construction.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(construction.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewConstruction(construction.constructionId)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={constructions.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Container>
  );
};